import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import { useAuth0 } from "@auth0/auth0-react";
import request from "@/lib/api/handler";
import {
  GeneralCandidateAvailability,
  SpecificCandidateAvailability,
  weekdayList,
} from "@/types";
import {
  DayAvailabilities,
  SpecificAvailabilities,
  useAvailabilitySheet,
} from "@/components/dialogs/candidate/availability/store";

export const useCompanyCandidateAvailabilityQuery = (
  openingID = "",
  interviewID = "",
  candidateID = "",
) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.candidateAvailability(openingID, interviewID),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const resp = await request<{
        general: GeneralCandidateAvailability[];
        specific: SpecificCandidateAvailability[];
      }>({
        method: "GET",
        path: `/api/interview/${interviewID}/availability/${candidateID}`,
        token,
      });
      let tzo = "";

      const general = Array.from(
        { length: 7 },
        () => [],
      ) as DayAvailabilities[];
      resp.general.forEach((a) => {
        if (!tzo) {
          tzo = a.timezone ?? "America/Chicago";
        }

        general[weekdayList.indexOf(a.weekday)]?.push({
          id: a.id,
          startTime: a.startTime,
          endTime: a.endTime,
          timezone: a.timezone,
        });
      });

      const specific = resp.specific
        .filter((e) => e.dates !== undefined)
        .reduce((acc, curr) => {
          if (!tzo) {
            tzo = curr.timezone ?? "America/Chicago";
          }

          const i = acc.findIndex(
            (e) =>
              e.dates.length === curr.dates.length &&
              e.dates.every((eDate) =>
                curr.dates.some((currDate) => currDate === eDate),
              ),
          );
          if (i < 0) {
            acc.push({
              dates: curr.dates,
              times: [
                {
                  id: curr.id,
                  startTime: curr.startTime,
                  endTime: curr.endTime,
                  timezone: curr.timezone,
                },
              ],
            });
          } else {
            acc[i]?.times.push({
              id: curr.id,
              startTime: curr.startTime,
              endTime: curr.endTime,
              timezone: curr.timezone,
            });
          }

          return acc;
        }, [] as SpecificAvailabilities[]);

      if (!general.some((e) => e.length > 0)) {
        for (let i = 1; i < 6; i++) {
          general[i] = [
            {
              startTime: "",
              endTime: "",
              timezone: tzo,
              placeholder: true,
            },
          ];
        }
      }

      general.forEach((e, i) => {
        general[i] = e.sort((a, b) => a.startTime.localeCompare(b.startTime));
      });

      specific.forEach((e) => e.dates.sort((a, b) => a.localeCompare(b)));
      specific.sort((a, b) => a.dates[0]!.localeCompare(b.dates[0]!));
      specific.forEach((e, i) => {
        specific[i]!.times = e.times.sort((a, b) =>
          a.startTime.localeCompare(b.startTime),
        );
      });

      useAvailabilitySheet.getState().setDayAvailabilities(general);
      useAvailabilitySheet.getState().setSpecificAvailabilities(specific);
      if (tzo) {
        useAvailabilitySheet.getState().setTimezone(tzo);
      }
      return { general, specific };
    },
    enabled: !!openingID && !!interviewID,
  });

  return {
    candidateAvailabilityLoading: isLoading,
    candidateAvailability: data,
    candidateAvailabilityError: error,
  };
};
